/**
 * Pure builder for the /books JSON-LD @graph.
 * Imported by both src/pages/Books.tsx (runtime) and scripts/validate-books-jsonld.ts (build-time),
 * so what we validate is exactly what ships.
 */

import { organizationNode, personNode } from "./entity-jsonld";

export interface BookInput {
  format: string;
  title: string;
  subtitle: string;
  desc: string;
  image: string;
  alt?: string;
  featured?: boolean;
  buyLinks?: ReadonlyArray<{ label: string; href: string }>;
}

export const BOOKS_SITE_URL = "https://drsullyspeaks.com";

const slug = (s: string) =>
  s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

const isEbookLabel = (l: string) => /ebook|epub|kindle|digital/i.test(l);

const abs = (u: string, site = BOOKS_SITE_URL) =>
  u.startsWith("http") ? u : `${site}${u}`;

export function buildBooksJsonLd(
  books: ReadonlyArray<BookInput>,
  site: string = BOOKS_SITE_URL,
) {
  const AUTHOR_ID = `${site}/#person`;
  const ORG_ID = `${site}/#organization`;


  const bookNodes = books.map((b) => {
    const id = `${site}/books#${slug(b.title)}`;
    const workExample = (b.buyLinks ?? []).map((link) => {
      const ebook = isEbookLabel(link.label);
      return {
        "@type": "Book",
        "@id": `${id}#${ebook ? "ebook" : "paperback"}`,
        bookFormat: ebook
          ? "https://schema.org/EBook"
          : "https://schema.org/Paperback",
        inLanguage: "en",
        url: link.href,
        potentialAction: {
          "@type": "BuyAction",
          target: {
            "@type": "EntryPoint",
            urlTemplate: link.href,
            actionPlatform: [
              "https://schema.org/DesktopWebPlatform",
              "https://schema.org/MobileWebPlatform",
            ],
          },
        },
      };
    });
    return {
      "@type": "Book",
      "@id": id,
      name: b.title,
      alternateName: b.subtitle,
      description: b.desc,
      image: abs(b.image, site),
      inLanguage: "en",
      author: { "@id": AUTHOR_ID },
      publisher: { "@id": ORG_ID },
      brand: { "@id": ORG_ID },
      url: `${site}/books`,
      ...(workExample.length ? { workExample } : {}),
    };
  });

  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        ...organizationNode(),
        "@id": ORG_ID,
        url: site,
        founder: { "@id": AUTHOR_ID },
      },
      {
        ...personNode(),
        "@id": AUTHOR_ID,
        url: `${site}/about`,
        worksFor: { "@id": ORG_ID },
      },

      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: site },
          { "@type": "ListItem", position: 2, name: "Books", item: `${site}/books` },
        ],
      },
      {
        "@type": "ItemList",
        name: "Books by Dr. Sully",
        numberOfItems: books.length,
        itemListOrder: "https://schema.org/ItemListOrderAscending",
        itemListElement: bookNodes.map((n, i) => ({
          "@type": "ListItem",
          position: i + 1,
          url: `${site}/books`,
          item: { "@id": n["@id"] as string },
        })),
      },
      ...bookNodes,
    ],
  };
}
